import {
  useState,
  useCallback,
  type ComponentProps,
  ReactNode,
  CSSProperties,
} from "react";
import { type UseFormRegisterReturn } from "react-hook-form";
import styles from "./Input.module.css";
import Label from "components/Label";
import FieldError from "components/FieldError/FieldError";
import FormFieldWrapper from "components/FormFieldWrapper/FormFieldWrapper";
import Icon from "components/Icon";

type PasswordInputProps = ComponentProps<"input"> & {
  label: string;
  register: UseFormRegisterReturn;
  error?: string;
  required?: boolean;
  content?: string;
  hasTooltip?: boolean;
  labelAction?: ReactNode;
  containerStyle?: CSSProperties;
};

export default function PasswordInput({
  label,
  register,
  error,
  required,
  hasTooltip,
  content,
  labelAction,
  containerStyle,
  ...props
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  // Function to render label conditionally
  const renderLabel = () => {
    if (!label) return null;
    return (
      <Label required={required} tooltipContent={content}>
        {label}
      </Label>
    );
  };

  return (
    <FormFieldWrapper
      label={renderLabel()}
      error={<FieldError error={error} />}
      containerStyle={containerStyle}
      labelAction={labelAction}
    >
      <div className={styles["password__icon--container"]}>
        <input
          id={props.id}
          type={showPassword ? "text" : "password"}
          {...props}
          className={styles["form__input"]}
          {...register}
          aria-label="password"
        />
        <Icon
          name={showPassword ? "eyeOff" : "eyeOpen"}
          onClick={togglePasswordVisibility}
          className={styles["password__icon"]}
          tabIndex={-1}
        />
      </div>
    </FormFieldWrapper>
  );
}
